import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useCreateCustomer } from '@/hooks/useCustomerType';
import {
  generateSecurePassword,
  generateUsername,
} from '@/sections/customerManagement/components/modals/AddCustomerModal/utils';
import type { CatalogProgram } from '@/sections/customerManagement/types';
import { configureBugBountyIntegration } from '@/sections/customerManagement/utils/configureBugBountyIntegration';
import { useAuth } from '@/state/auth';

export function useApproveProgram() {
  const [isPending, setIsPending] = useState(false);
  const createCustomer = useCreateCustomer();
  const axios = useAxios();
  const { getToken, refreshAuth } = useAuth();

  const approveProgram = useCallback(
    async (program: CatalogProgram) => {
      setIsPending(true);
      try {
        const username = generateUsername(program.name);
        const password = generateSecurePassword();

        let profilePicture: ArrayBuffer | null = null;
        if (program.profile_picture_url) {
          try {
            const imgResp = await fetch(program.profile_picture_url);
            const contentType = imgResp.headers.get('content-type') ?? '';
            if (imgResp.ok && contentType.startsWith('image/')) {
              profilePicture = await imgResp.arrayBuffer();
            }
          } catch {
            // Best-effort -- proceed without profile picture
          }
        }

        await createCustomer.mutateAsync({
          username,
          password,
          settings_display_name: program.name,
          customer_type: 'BUG_BOUNTY',
          collaborators: [],
          allowed_domains: ['praetorian.com'],
          profile_picture: profilePicture,
          scan_level: 'A',
          enabled_flags: [
            'enable_external',
            'enable_ai_agents',
            'enable_ai_automation',
            'disable_aiml_training',
          ],
        });

        const token = await getToken();
        const scopedHeaders = {
          Authorization: `Bearer ${token}`,
          Account: username,
        };

        // Run post-onboard configuration in parallel -- all best-effort
        await Promise.allSettled([
          configureBugBountyIntegration(
            axios,
            username,
            'hackerone',
            program.handle,
            token
          ),
          // 2-week subscription period of performance
          axios
            .put(
              '/configuration',
              {
                name: 'subscription',
                value: {
                  startDate: new Date().toISOString().split('T')[0],
                  endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
                    .toISOString()
                    .split('T')[0],
                },
              },
              { headers: scopedHeaders }
            )
            .catch(err => {
              console.warn('Failed to create subscription:', err);
            }),
          // Enable web application scanning
          axios
            .put(
              '/configuration',
              { name: 'enable_burp_module', value: 50 },
              { headers: scopedHeaders }
            )
            .catch(err => {
              console.warn('Failed to enable web scanning:', err);
            }),
        ]);

        try {
          await axios.put(
            '/setting',
            { name: 'hackerone_handle', value: program.handle },
            { headers: scopedHeaders }
          );
        } catch {
          // Best-effort -- the customer is already created
        }

        await refreshAuth();

        toast.success(
          `Bug bounty program "${program.name}" approved and scope import started.`
        );
      } catch (err) {
        toast.error(
          `Failed to approve "${program.name}": ${err instanceof Error ? err.message : 'Unknown error'}`
        );
      } finally {
        setIsPending(false);
      }
    },
    [createCustomer, axios, getToken, refreshAuth]
  );

  return { approveProgram, isPending };
}
